import { ConfigService, RunConfigService, FirebaseUtil, SponsorStorageService, MarketSiteRegionsV2 } from '@nuskin/ns-util';
import { UserService } from '@nuskin/ns-account';
import { TdcStrings } from '@nuskin/ns-util';
import { MySiteData, MySiteBanner, FeaturedProduct, MySiteBundle } from './mySiteDtos';
import { PriceType } from '@nuskin/ns-shop';

const FIREBASE_URL = 'https://{SUBDOMAIN}.firebaseio.com';
const SUBDOMAINS_PATH = 'storefront/subdomains';
const SPONSORS_PATH = 'storefront/users';

let sponsorIds = new Map();
let sponsorIdPromises = new Map();
let sponsors = new Map();
let sponsorPromises = new Map();
let marketBanners = new Map();
let marketProducts = new Map();
let marketProductPromises = new Map();

let user;
let firebase;
let userFirebaseConn;
let anonFirebaseConn;

let blacklistOfEquals;
let blacklistOfContains;


/**
 * Service facilitating communication with Firebase for My Site and Personal Offer data.
 *
 * In Firebase, a given My Site subdomain is tied to the distributor ID of its sponsor. This also applies to subdomains that have been imported from the Nu Skin OPS program.
 * Elsewhere in Firebase, sponsor records are maintained for distributors with My Site stores and/or Personal Offers. These records include profile information and My Site store settings.
 * This service provides read/write access to this data as it pertains to My Site.
 * @namespace MySiteRestService
 */
export default {
    getCurrentSubdomain,
    getCurrentSponsorId,
    getSponsorId,
    getSubdomainsBySponsorId,
    getSponsorData,
    getMySiteStoreData,
    getMarketFeaturedProducts,
    getMarketFeaturedBundle,
    getMarketMySiteConfig,
    isSubdomainBlacklisted,
    isSubdomainAvailable,
    getMarketBanners,
    saveMarketBanners,
    uploadMarketBannerImage,
    deleteMarketBannerImage,
    initialSponsorSetup,
    removeSubdomain,
    updateSponsorData,
    changeSubdomain,
    saveFullProductCatalog,
    setMySitePublished,
    setSocialMediaAccounts,
    uploadPhoto,
    removePhoto,
    saveFeaturedProducts,
    saveBanner,
    saveBundles,
    touchLanguage,
    updateMarketAndLanguageSpecificData,
    getEnabledMarketsAndLanguages,
    saveMySiteLogos,
    savePriceType,
    reset,
    getMySiteNav
};



//=================================================================//
//
//  PUBLIC FUNCTIONS
//
//=================================================================//

/**
 * Returns the subdomain of the My Site store the current page is on. Returns null if no My Site subdomain can be found.
 * @returns {String} Subdomain name
 * @memberof MySiteRestService
 * @public
 */
function getCurrentSubdomain() {
    let fragments = window.location.hostname.split('.');
    
    if ((fragments.length > 4 && /^(sbx|dev|tst|prd)$/.test(fragments[2]))
        || (fragments.length === 4 && /^(dev|test)$/.test(fragments[1]))
        || (fragments.length === 3 && !/^(www|dev|test)$/.test(fragments[0]))
        || (fragments.length === 2 && fragments[1] === 'localhost')) {
        return fragments[0];
    }

    return null;
}

/**
 * Retrieves the ID of the sponsor for the My Site store the current page is on.
 * Attempts to retrieve this value first from the url, then from local storage, and then finally from Firebase through a query for the current subdomain.
 * Resolves to null if no sponsor ID can be found.
 * @returns {Promise.<String>} Promise that resolves to sponsor ID or null if none found
 * @memberof MySiteRestService
 * @public
 */
async function getCurrentSponsorId() {
    let id = null;
    // Get sponsor of subdomain from Firebase
    let subdomain = getCurrentSubdomain();
    let subdomainSponsor;
    if (subdomain && subdomain !== 'mysite') {
        subdomainSponsor = await getSponsorId(subdomain);
    }

    if(subdomain === 'mysite') {
        // Get sponsor ID from url if present
        let queryParams = window.location.search.split('&');
        let sponsorString = queryParams.find(p => /storeId=/.test(p));
        if (sponsorString) id = sponsorString.substring(sponsorString.indexOf('=') + 1);

        // If sponsor ID not in url, get from local storage
        if (!id) id = SponsorStorageService.getSponsor();
    }

    // If sponsor ID found in url or local storage, validate against sponsor of subdomain
    if ((subdomainSponsor && id && subdomainSponsor !== id) || (!subdomainSponsor && !id)) {
        throw new Error(`Subdomain ${subdomain} is not registered to sponsor ${id}`);
    }
    if (subdomainSponsor) {
        id = subdomainSponsor;
    }

    // Save and return sponsor ID
    SponsorStorageService.setSponsor(id);
    return id;
}

/**
 * Returns the distributor ID associated with the given My Site subdomain.
 * Returns null if no ID is found for the given subdomain.
 * @param {String} subdomain - My Site subdomain
 * @returns {Promise.<String>} Promise that resolves to distributor ID or null if no data is available
 * @memberof MySiteRestService
 * @public
 */
async function getSponsorId(subdomain) {
    if (!subdomain) {
        //Get the subdomain off the current url
        subdomain = window.location.hostname.split(".")[0];
    }

    // Return cached data if available
    if (sponsorIds.has(subdomain)) return sponsorIds.get(subdomain);
    if (sponsorIdPromises.has(subdomain)) return sponsorIdPromises.get(subdomain);

    // No cached data available; retrieve from server.
    let url = `${getFirebaseUrl()}/${SUBDOMAINS_PATH}/${subdomain}.json`;
    let promise = fetch(url, { method: 'GET', credentials: 'omit' }).then(transformFetchResponse);
    sponsorIdPromises.set(subdomain, promise);

    let id = await promise;
    sponsorIds.set(subdomain, id);
    sponsorIdPromises.delete(subdomain);

    return id;
}

/**
 * Retrieves any subdomains registered to the given distributor ID (or to the current user if no ID is provided).
 * Returns these subdomain names as a list. Returns an empty list if no subdomains are found registered to the given distributor.
 * @param {String} id - Distributor ID; required if not retrieving data of current authenticated user
 * @returns {Promise<Array<String>>} Promise that resolves to list of subdomain names
 * @throws {Error} if no ID is provided AND the current user is unauthenticated
 * @memberof MySiteRestService
 * @public
 */
async function getSubdomainsBySponsorId(id) {
    id = id || fallbackToCurrentUserId(id);
    let url = `${getFirebaseUrl()}/${SUBDOMAINS_PATH}.json?orderBy="$value"&equalTo="${id}"`;
    let data = await fetch(url, { method: 'GET', credentials: 'omit' }).then(transformFetchResponse);
    return Object.keys(data).filter(key => data[key] === id);
}

/**
 * Returns the My Site sponsor record for the given distributor ID (or for the current user if no ID is provided).
 * Returns null if no sponsor record is found.
 * @param {String} [id] - My Site sponsor's distributor ID; required if not retrieving data of current authenticated user
 * @param {Boolean} [forceRefresh=false] - Flag indicating whether to force a request for fresh data from Firebase
 * @returns {Promise.<>} Promise that resolves to {@link } object or null if no data is available
 * @throws {Error} if no ID is provided AND the current user is unauthenticated
 * @memberof MySiteRestService
 * @public
 */
async function getSponsorData(id, forceRefresh) {
    id = id || fallbackToCurrentUserId();

    // Return cached data if available and not forcing a refresh
    if (sponsors.has(id) && !forceRefresh) return cloneSponsorRecord(sponsors.get(id));

    // Return cached promise if server request is currently in progress
    if (sponsorPromises.has(id)) return sponsorPromises.get(id);

    // No cached data and no cached request; make new server request
    let url = `${getFirebaseUrl()}/${SPONSORS_PATH}/${id}.json`;
    let promise = fetch(url, { method: 'GET', credentials: 'omit' }).then(transformFetchResponse)
        .then(data => {
            sponsors.set(id, data || null);
            sponsorPromises.delete(id);
            return cloneSponsorRecord(sponsors.get(id));
        });
    sponsorPromises.set(id, promise);
    return promise;
}

/**
 * Returns the My Site store data associated with the given distributor ID, subdomain, and country code.
 * Returns null if a My Site store cannot be found matching the given parameters.
 * @param {String} id - Sponsor ID
 * @param {String} subdomain - My Site subdomain
 * @param {String} country - Country code
 * @returns {Promise.<MySiteData>} Promise that resolves to My Site store data
 * @memberof MySiteRestService
 * @public
 * @see MySiteData
 */
async function getMySiteStoreData(id, subdomain, country) {
    let sponsor = await getSponsorData(id);
    if (!sponsor) return null;
    let storefronts = sponsor.storefront || {};
    if (!storefronts[subdomain]) return null;
    if (!storefronts[subdomain][country]) return null;

    return storefronts[subdomain][country];
}

/**
 * Returns a market's list of featured products.
 * @param {Object} [options] - optional parameters:
 * @param {String} [options.country] - Country code; defaults to current country
 * @param {String} [options.language] - Language code; defaults to current language
 * @returns {Promise.<Array<FeaturedProduct>>} Promise that resolves to list of products
 * @memberof MySiteRestService
 * @public
 * @see FeaturedProduct
 */
async function getMarketFeaturedProducts(options) {
    let data = await getMarketFeaturedProductsData(options);
    let products = data['products'] || [];
    return products.map(p => new FeaturedProduct(p));
}

/**
 * Returns a market's featured product bundle.
 * @param {Object} [options] - optional parameters:
 * @param {String} [options.country] - Country code; defaults to current country
 * @param {String} [options.language] - Language code; defaults to current language
 * @returns {Promise.<Array<FeaturedProduct>>} Promise that resolves to list of products
 * @memberof MySiteRestService
 * @public
 * @see FeaturedProduct
 */
async function getMarketFeaturedBundle(options) {
    let data = await getMarketFeaturedProductsData(options);
    let bundle = data['bundles'] || [];
    return bundle.map(p => p.sku);
}

/**
 * Checks the given subdomain against a blacklist.
 * Returns true if the subdomain contains blacklisted words, false otherwise.
 * @param {String} subdomain - My Site subdomain to check against blacklist
 * @returns {Promise.<Boolean>} Promise that resolves to boolean
 * @memberof MySiteRestService
 * @public
 */
async function isSubdomainBlacklisted(subdomain) {
    if (!subdomain) throw new Error('Null Argument Error');
    blacklistOfContains = blacklistOfContains || await fetch(`${getFirebaseUrl()}/storefront/blacklistedContains.json`, { method: 'GET', credentials: 'omit' }).then(transformFetchResponse);
    blacklistOfEquals = blacklistOfEquals || await fetch(`${getFirebaseUrl()}/storefront/blacklistedEquals.json`, { method: 'GET', credentials: 'omit' }).then(transformFetchResponse);

    let sub = subdomain.toLowerCase();
    return (blacklistOfEquals.map(word => word.toLowerCase()).includes(sub))
        || blacklistOfContains.some(word => sub.includes(word.toLowerCase()));
}

/**
 * Checks the given subdomain against existing subdomains, to determine whether it is already taken.
 * Returns true if the given subdomain is available (not already taken), false otherwise.
 * @param {String} subdomain - My Site subdomain to check
 * @returns {Promise.<Boolean>} Promise that resolves to boolean
 * @memberof MySiteRestService
 * @public
 */
async function isSubdomainAvailable(subdomain) {
    let id = await getSponsorId(subdomain);
    return !id || id === getUser().id;
}

/**
 * Retrieves the current market's My Site banners from Firebase.
 * Returns an empty array if the market has no banners.
 * @returns {Promise<Array<MySiteBanner>>}
 * @memberof MySiteRestService
 * @public
 */
async function getMarketBanners(ctry, lng) {
    let cfg = RunConfigService.getRunConfig();
    ctry = ctry || cfg.country;
    lng = lng || cfg.language;

    let mkt = `${lng}_${ctry}`;
    if (marketBanners.has(mkt)) return marketBanners.get(mkt);

    let url = `${getFirebaseUrl()}/storefront/storefrontDefaults/${ctry}/lang/${lng}/banners.json`;
    let data = await fetch(url, { method: 'GET', credentials: 'omit' }).then(transformFetchResponse);
    let banners = (data && (data instanceof Array)) ? data.map(b => new MySiteBanner(b)) : [];
    marketBanners.set(mkt, banners);
    return banners;
}

/**
 * Overwrites the current market's My Site banners with the given data.
 * Returns the updated banners list.
 * @param {Array<MySiteBanner>} banners
 * @returns {Promise<Array<MySiteBanner>>}
 * @memberof MySiteRestService
 * @public
 */
async function saveMarketBanners(banners) {
    let cfg = RunConfigService.getRunConfig();
    let mkt = `${cfg.language}_${cfg.country}`;
    let fb = await getAnonFirebaseConn();
    let ref = fb.database().ref(`/storefront/storefrontDefaults/${cfg.country}/lang/${cfg.language}/banners`);
    await ref.set(banners);
    let updatedBanners = await ref.once('value')
        .then(snapshot => snapshot.val())
        .then(data => (data || []).map(val => new MySiteBanner(val)));
    marketBanners.set(mkt, updatedBanners);
    return updatedBanners;
}

/**
 * Saves the given hero banner image to Firebase and returns the image's download url.
 * This method is intended for use with market default My Site banners.
 * @param {File} imgFile - image file object
 * @returns {Promise<String>} Promise that resolves to image's download url
 */
async function uploadMarketBannerImage(imgFile) {
    let fb = await getAnonFirebaseConn();
    let storageRef = fb.storage().ref();

    let metadata = { contentType: imgFile.type || 'image/png' };

    let cfg = RunConfigService.getRunConfig();
    let childRef = storageRef.child(`banners/markets/${cfg.language}_${cfg.country}/${imgFile.name}`);
    await childRef.put(imgFile, metadata);
    return childRef.getDownloadURL();
}

/**
 * Removes the image corresponding to the given download url from Firebase.
 * This method is intended for use with market default My Site banners.
 * @param {String} imgUrl - image url
 * @returns {Promise.<void>} Promise that resolves if deletion was successful.
 */
async function deleteMarketBannerImage(imgUrl) {
    let fb = await getAnonFirebaseConn();
    let imageRef = fb.storage().refFromURL(imgUrl);
    if (!imageRef) throw new Error(`Cannot find Firebase reference for url ${imgUrl}`);
    return imageRef.delete();
}

async function getMarketMySiteConfig(country, language) {
    let result = {}
    let runCfg = RunConfigService.getRunConfig();
    let url = runCfg.baseUrl + '/content/nuskin/' + language + '_' + country + '/tools/mysite.infinity.3.json';
    let data = await fetch(url, { method: 'GET' }).then(transformFetchResponse);
    if(data && data['jcr:content'] && data['jcr:content'].bodyContent) {
        Object.keys(data['jcr:content'].bodyContent).forEach(key => {
            if(key.indexOf('mysitewrapper') !== -1) result = data['jcr:content'].bodyContent[key]
        })
    }
    return result
}

async function getMySiteNav(country, language) {
    let result = []
    let runCfg = RunConfigService.getRunConfig();
    let url = `${runCfg.baseUrl}/content/nuskin/${language}_${country}/jcr:content/nsHeaderStoreFront/mySiteNavigation.json`
    let data = await fetch(url, { method: 'GET' }).then(transformFetchResponse)
    for(let i = 0; i<data.mySiteNavigation.length; i++) {
        let path = data.mySiteNavigation[i].replace(/\{.+":"|"\}/g,'')
        url = `${runCfg.baseUrl}${path}/jcr:content/jcr:title.json`
        let res = await fetch(url, { method: 'GET' }).then(transformFetchResponse)
        result.push(res['jcr:title'])
    }
    return result
}

/**
 * Sets up the current user as a My Site sponsor for the given subdomain and the current country in the current language
 * @param {String} subdomain - My Site subdomain to initialize
 * @param {} data - My Site sponsor data
 * @param {String} storeName - My Site store name
 * @param {String} oldSubdomain - Existing subdomain assigned to user ID
 * @param {String} country - Country
 * @param {String} language - Language within the market
 * @param {Boolean} defaultLang - Identifies whether language needs to be set as default
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 */
async function initialSponsorSetup(subdomain, data, storeName, oldSubdomain, country, language, defaultLang, fullProductCatalog) {
    let fb = await getUserFirebaseConn();
    let cfg = RunConfigService.getRunConfig();
    country = country || cfg.country;
    language = language || cfg.language;

    let featuredProducts = await getMarketFeaturedProducts({ country, language });

    // Create My Site store data in sponsor record and prefill with default values
    data.storefront = data.storefront || {};
    data.storefront[subdomain] = data.storefront[subdomain] || {};
    data.storefront[subdomain][country] = data.storefront[subdomain][country] || {};
    data.storefront[subdomain][country].products = data.storefront[subdomain][country].products || featuredProducts.map(item => item.sku);

    // GET DEFAULTS FROM AEM
    let defaultBundle = await getMarketFeaturedBundle({country});
    let defaultBundleId = 0; //default bundle id is the same across markets/languages

    data.storefront[subdomain][country].bundles = data.storefront[subdomain][country].bundles || [
        {
            id: defaultBundleId,
            skus: defaultBundle
        }
    ];

    let marketBanners = await this.getMarketBanners(country, language);
    let defaultBanner = marketBanners[0]; // default banner is indexed 0;

    let mySiteTranslationsTdc = await TdcStrings.getStrings('mysite/mysite-home', language, country) || {};
    let mySiteTranslationsNonTdc = await TdcStrings.getNonTdcStrings('mysite/mysite-home', language, country) || {};
    if(!Object.keys(mySiteTranslationsTdc).length)
        throw new Error(`My Site Home Page was not setup for ${language}_${country} in AEM`);

    data.useDefaultLogos = data.useDefaultLogos !== undefined ? data.useDefaultLogos : true;
    data.profilePhoto = data.profilePhoto || mySiteTranslationsNonTdc.welcomeSectionProfileImageReference;
    data.siteLogo = data.siteLogo || mySiteTranslationsNonTdc.welcomeSectionLogoImageReference;

    data.storefront[subdomain][country].showDefaultBundles = data.storefront[subdomain][country].showDefaultBundles !== undefined ? data.storefront[subdomain][country].showDefaultBundles : true;
    data.storefront[subdomain][country].showDefaultProducts = data.storefront[subdomain][country].showDefaultProducts !== undefined ? data.storefront[subdomain][country].showDefaultProducts : true;

    data.storefront[subdomain][country].lang = data.storefront[subdomain][country].lang || {};

    if(fullProductCatalog === undefined) {
        let runCfg = RunConfigService.getRunConfig();
        let url = runCfg.baseUrl + '/content/nuskin/' + language + '_' + country + '/tools/mysite/jcr:content/bodyContent/mysitewrapper_222c.json';
        let config = await fetch(url, { method: 'GET' }).then(transformFetchResponse);
        fullProductCatalog = config.fullProductCatalogFormShowSearch && config.fullProductCatalogFormShowSearch === 'true';
    }

    data.storefront[subdomain][country].lang[language] = data.storefront[subdomain][country].lang[language] || {
        default: !!defaultLang,
        fullProductCatalog: !!fullProductCatalog,
        wholesale: !!mySiteTranslationsNonTdc.priceTypeWholesaleDefault,
        storeName: storeName || mySiteTranslationsTdc.welcomeSectionSiteName || '',
        aboutYourStore: mySiteTranslationsTdc.welcomeSectionAboutYourSite || '',
        purchaseMessage: mySiteTranslationsTdc.welcomeSectionPurchase || '',
        aboutProducts: mySiteTranslationsTdc.featuredProductsDescription || '',
        banner: defaultBanner || '',
        bundlesInfo: [
            {
                default: !!defaultLang,
                bundle_id: defaultBundleId,
                name: mySiteTranslationsTdc.featuredBundleTitle || '',
                about: mySiteTranslationsTdc.defaultBundlesMessage || ''
            }
        ]
    };
    // Validate subdomain against blacklist and duplication
    if (!await validateSubdomain(subdomain, fb)) return;

    // remove old subdomain if it doesnt match the new one
    if (oldSubdomain && oldSubdomain !== subdomain) {
        await removeSubdomain(oldSubdomain, fb);
    }
    // Save sponsor record
    return updateSponsorData(data);
}


async function validateSubdomain(subdomain, fb) {
    let valid = true;
    try {
        let isBlacklisted = await isSubdomainBlacklisted(subdomain);
        if (isBlacklisted) throw new Error(`Subdomain ${subdomain} is blacklisted`);

        // Attempt to register subdomain to this distributor
        let existingSponsorId = await getSponsorId(subdomain);
        let userId = getUser().id
        
        if (!existingSponsorId || (existingSponsorId === userId)) {
            // Subdomain is available; register it to this distributor
            await fb.database().ref(`/${SUBDOMAINS_PATH}/${subdomain}`).set(userId);
        } else if (existingSponsorId !== userId) {
            // Subdomain is registered to another distributor
            throw new Error(`Subdomain ${subdomain} is not available to this user`);
        }
    } catch (err) {
        valid = false;
    }
    return valid;
}

/**
 * Removes subdomain from firebase
 * @param {*} subdomain - subdomain to remove
 * @param {*} fb - firebase connection
 */
async function removeSubdomain(subdomain, fb) {
    return fb.database().ref(`/${SUBDOMAINS_PATH}/${subdomain}`).set(null);
}

/**
 * Updates the current user's My Site sponsor record in Firebase with the given data.
 * Returns the updated sponsor record.
 * @param {} sponsor - Sponsor record data to save
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @throws {Error} if the current user is unauthenticated
 * @memberof MySiteRestService
 * @public
 */
async function updateSponsorData(sponsor) {
    let fb = await getUserFirebaseConn();
    await fb.database().ref(`/${currentUserPath()}`).update(sponsor);
    return updateCachedSponsorData();
}

/**
 * Changes Distributors storefront subdomain
 * @param {String} subdomain - new desired subdomain
 */
async function changeSubdomain(subdomain) {
    let fb = await getUserFirebaseConn();

    if (!await validateSubdomain(subdomain, fb)) return;

    let sponsor = await getSponsorData();

    let storefrontKeys = Object.keys(sponsor.storefront);
    let storefrontData = sponsor.storefront[storefrontKeys[0]];

    if (storefrontKeys[0] !== subdomain) {
        await removeSubdomain(storefrontKeys[0], fb); // Delete old subdomain
    }

    sponsor.storefront = {
        [subdomain]: storefrontData
    }

    return updateSponsorData(sponsor);
}

/**
 * @param {Boolean} value
 * @memberof MySiteRestService
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @public
 */
async function saveFullProductCatalog(value, country, language, subdomain) {
    let fb = await getUserFirebaseConn();
    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/lang/${language}/fullProductCatalog`).set(!!value);
    return updateCachedSponsorData();
}

/**
 * @param {Boolean} value
 * @memberof MySiteRestService
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @public
 */
async function savePriceType(value, country, language, subdomain) {
    let fb = await getUserFirebaseConn();
    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/lang/${language}/wholesale`).set(!!value);
    return updateCachedSponsorData();
}

/**
 * Sets whether the current user's given My Site store is published.
 * @param {String} subdomain - My Site store subdomain
 * @param {String} country - Country code
 * @param {Boolean} publish - Flag indicating whether to publish the given My Site store
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @throws {Error} if the current user is unauthenticated
 * @memberof MySiteRestService
 * @public
 */
async function setMySitePublished(subdomain, country, publish) {
    let fb = await getUserFirebaseConn();
    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}`).update({ 'published': publish });
    return updateCachedSponsorData();
}

/**
 * Sets the given {@link MySiteSocialAccount}s as the social media accounts for the current user's My Site store.
 * @param {Array<MySiteSocialAccount>} accounts - List of social media account details
 * @returns {Promise.<>} Promise that resolves to updated My Site sponsor record
 */
async function setSocialMediaAccounts(accounts) {
    let fb = await getUserFirebaseConn();
    await fb.database().ref(`/${currentUserPath()}/socialAccounts`).set(toCleanedObj(accounts));
    return updateCachedSponsorData();
}

/**
 * Saves the given profile image to Firebase and returns the image's download url.
 * This method is intended for use with My Site sponsor profile images.
 * @param {File} file - image file object
 * @returns {Promise<String>} Promise that resolves to image's download url
 */
async function uploadPhoto(file, type) {
    let fb = await getUserFirebaseConn();
    let storageRef = fb.storage().ref();
    let childRef = storageRef.child(`profilePic/${file.name}-mysite-${type}`);
    await childRef.put(file, { contentType: file.type || 'image/png' });
    return childRef.getDownloadURL();
}

/**
 * Removes the image corresponding to the given download url from Firebase.
 * This method is intended for use with My Site sponsor profile images.
 * @param {String} imgUrl - image url
 * @returns {Promise.<void>} Promise that resolves if deletion was successful.
 */
async function removePhoto(imgUrl) {
    let fb = await getUserFirebaseConn();
    let imageRef = fb.storage().refFromURL(imgUrl);
    if (!imageRef) throw new Error(`Cannot find Firebase reference for url ${imgUrl}`);
    return imageRef.delete();
}


async function saveMySiteLogos(profilePhoto, siteLogo, profilePhotoFile, siteLogoFile, useDefaultLogos) {
    if(!useDefaultLogos) {
        if (profilePhotoFile)
            profilePhoto = await uploadPhoto(profilePhotoFile, 'profile');
        if (siteLogoFile)
            siteLogo = await uploadPhoto(siteLogoFile, 'site-logo');
    }

    let data = {
        profilePhoto,
        siteLogo,
        useDefaultLogos
    };

    return await updateSponsorData(data);
}

/**
 * Saves skus of featured products
 * @param {Array<String>} skus - list of products skus.
 * @param {String} aboutProducts - featured products description
 * @returns {Promise}
 */
async function saveFeaturedProducts(skus, aboutProducts, ctry, lng, showDefaultProducts) {
    let fb = await getUserFirebaseConn();
    let user = UserService.getUser();
    let subdomains = await getSubdomainsBySponsorId(user.id);
    let subdomain = subdomains[0];
    let runCfg = RunConfigService.getRunConfig();
    let country = ctry || runCfg.country;
    let language = lng || runCfg.language;

    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/showDefaultProducts`).set(showDefaultProducts);
    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/products`).set(skus);
    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/lang/${language}/aboutProducts`).set(aboutProducts);

    return updateCachedSponsorData();
}

async function updateMarketAndLanguageSpecificData(country, language, data) {
    let fb = await getUserFirebaseConn();
    let user = UserService.getUser();
    let subdomains = await getSubdomainsBySponsorId(user.id);
    let subdomain = subdomains[0];

    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/lang/${language}`).update(toCleanedObj(data));

    return updateCachedSponsorData();
}

/**
 * Saves selected banner
 * @param {Object} banner - banner object
 * @returns {<Promise>}
 */
async function saveBanner(banner) {
    let fb = await getUserFirebaseConn();
    let user = UserService.getUser();
    let subdomains = await getSubdomainsBySponsorId(user.id);
    let subdomain = subdomains[0];

    let runCfg = RunConfigService.getRunConfig();
    let country = runCfg.country;
    let language = runCfg.language;

    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/lang/${language}/banner`).set(banner);
    return updateCachedSponsorData();
}

/**
 * Saves bundles
 * @param {Array<Object>} bundles - bundles list
 * @returns {<Promise>}
 */
async function saveBundles(bundles, bundlesInfo, country, language, showDefaultBundles) {
    let fb = await getUserFirebaseConn();

    let data = await getSponsorData();
    let subdomain = Object.keys(data.storefront)[0];

    if (bundles) {
        let currentBundles = data.storefront[subdomain][country].bundles || [];

        // check if some bundle(s) is/are being deleted. If so, remove translations for those/this bundle(s) for the whole market.
        currentBundles.forEach(async bundle => {
            let match = bundles.find(item => item.id === bundle.id);
            if (!match) {
                let bundleIdToRemove = bundle.id;
                let languages = data.storefront[subdomain][country].lang;

                for (let i = 0; i < Object.keys(languages).length; i++) {
                    let lang = Object.keys(languages)[i];
                    if(languages[lang].bundlesInfo && languages[lang].bundlesInfo.length) {
                        let filteredBundlesInfo = languages[lang].bundlesInfo.filter(bi => bi.bundle_id !== bundleIdToRemove);
                        await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/lang/${lang}/bundlesInfo`).set(filteredBundlesInfo);
                    }
                }
            }
        });

        bundles = bundles.map(b => new MySiteBundle(b)) || [];

        await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/bundles`).set(bundles);
    }

    if (bundlesInfo) {
        let languages = data.storefront[subdomain][country].lang || {};
        let remainingExistingLanguages = Object.keys(languages).filter(l => l !== language);

        // if new bundle(s) was/were added, then mark bundle(s) translations as default for the current market
        bundlesInfo.forEach(bInfo => {
            if(bInfo) {
                let match;
                remainingExistingLanguages.forEach(lang => {
                    if(languages[lang].bundlesInfo)
                        languages[lang].bundlesInfo.forEach(bi => {
                            if(bi) {
                                if (bi.bundle_id === bInfo.bundle_id) match = true;
                            }
                        });
                });

                if (!match) bInfo.default = true;
            }
        });

        await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/lang/${language}/bundlesInfo`).update(bundlesInfo);
    }

    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/showDefaultBundles`).set(showDefaultBundles);

    return updateCachedSponsorData();
}

async function touchLanguage(country, language, subdomain) {
    let fb = await getUserFirebaseConn();
    let today = new Date().toString();
    await fb.database().ref(`/${currentUserPath()}/storefront/${subdomain}/${country}/lang/${language}/lastEdited`).set(today);
    return updateCachedSponsorData();
}

async function getEnabledMarketsAndLanguages() {
    let runCfg = RunConfigService.getRunConfig();
    let data = await fetch(runCfg.baseUrl + '/content/nuskin/storefront-redirect/jcr:content/storefrontMarkets.json', {
        method: 'GET'
    }).then(transformFetchResponse);
    let regCtryLang = await MarketSiteRegionsV2.getMarketSiteRegionData();
    let result = {};

    // make a map of key(country code) => value(list of language codes)
    data.storefrontMarkets.forEach(item => {
        let lang = item.split('_')[0];
        let ctry = item.split('_')[1];

        if (result[ctry])
            result[ctry].languages.push(lang);
        else
            result[ctry] = {
                languages: [lang]
            }
    });

    // get country/langauge names by their codes
    Object.keys(result).forEach(ctryCode => {
        let country;
        let lng = [];
        regCtryLang.find(region => {
            country = region.countryname.find(c => c.countryCode === ctryCode);
            return country;
        });
        if(country) {
            result[ctryCode].displayName = country.locale_names[runCfg.language]
            result[ctryCode].defaultLocale = country.defaultLocale && country.defaultLocale.defaultLanguageCode 
                ? country.defaultLocale 
                : { defaultLanguageCode: country.languageCodes[0] };
            result[ctryCode].languages.forEach(language => {
                lng.push(
                    {
                        code: language,
                        displayName: country.displayLanguage[language]
                    }
                )
            });
            result[ctryCode].languages = lng;
        }
    });
    return result;
}


/**
 * Resets this service's cached data. This method is intended for use in
 * testing and is not recommended under normal use conditions.
 * @public
 */
function reset() {
    sponsorIds = new Map();
    sponsorIdPromises = new Map();
    sponsors = new Map();
    sponsorPromises = new Map();
    marketBanners = new Map();
    marketProducts = new Map();
    marketProductPromises = new Map();
}


//=================================================================//
//
//  PRIVATE FUNCTIONS
//
//=================================================================//

/**
 * Returns the current connection to Firebase
 * @returns {*}
 * @memberof MySiteRestService
 * @private
 */
function getFirebase() {
    if (!firebase) {
        let siteUrl = ConfigService.getMarketConfig().siteUrl;
        let env = (/test\.nuskin\.com/.test(siteUrl)) ? 'test' :
            (/www\.nuskin\.com/.test(siteUrl)) ? 'prod' : 'dev';
        FirebaseUtil.init(env);
        firebase = FirebaseUtil.getFB();
    }
    return firebase;
}

/**
 * Returns a connection to Firebase authenticated with the current user.
 * @returns {Promise.<*>}
 * @memberof MySiteRestService
 * @private
 */
async function getUserFirebaseConn() {
    anonFirebaseConn = null;
    if (!userFirebaseConn) {
        let user = getUser();
        let fb = await getFirebase();
        let fbSignIn = await FirebaseUtil.getSignInTokenWithEid(user.eid);
        await fb.auth().signInWithCustomToken(fbSignIn.token);
        userFirebaseConn = firebase;
    }
    return userFirebaseConn;
}

/**
 * Returns a connection to Firebase authenticated with an anonymous user.
 * @returns {Promise.<*>}
 * @memberof MySiteRestService
 * @private
 */
async function getAnonFirebaseConn() {
    userFirebaseConn = null;
    if (!anonFirebaseConn) {
        let beUrl = `${ConfigService.getMarketConfig().siteUrl}/simpleBe/api/v2/firebase/token/anonymous`;
        let headers = {
            'client_id': ConfigService.getMarketConfig().checkout.clientId,
            'client_secret': ConfigService.getMarketConfig().checkout.clientSecret
        };
        let tokenData = await fetch(beUrl, { method: 'GET', headers: headers, credentials: 'omit' })
            .then(transformFetchResponse);

        await getFirebase().auth().signInWithCustomToken(tokenData.Token.token);
        
        await getFirebase().auth().signInAnonymously();

        anonFirebaseConn = firebase;
    }
    return anonFirebaseConn;
}

/**
 * Returns the Firebase base url for the current market
 * @returns {String} Firebase url
 * @memberof MySiteRestService
 * @private
 */
function getFirebaseUrl() {
    return FIREBASE_URL.replace('{SUBDOMAIN}', ConfigService.getMarketConfig().storefrontFirebaseSubdomain);
}

/**
 * Returns the path of the current user's sponsor record in Firebase.
 * @returns {String}
 * @throws {Error} if the current user is unauthenticated
 * @memberof MySiteRestService
 * @private
 */
function currentUserPath() {
    let userId;

    let queryParams = window.location.search.split('&');
    let sponsorString = queryParams.find(p => /storeId=/.test(p));
    if (sponsorString) userId = sponsorString.substring(sponsorString.indexOf('=') + 1);
    
    if(!userId) {
        let user = UserService.getUser()
        userId = user.id;
    }

    return `${SPONSORS_PATH}/${userId}`;
}
/**
 * Returns the current user object.
 * @returns {Object}
 * @throws {Error} if the current user is unauthenticated.
 * @memberof MySiteRestService
 * @private
 */
function getUser() {
    user = user || UserService.getUser();
    if (!user)
        nuskin.events.publish(nuskin.events.authentication.REQUEST_FOR_LOGIN);
    return user;
}

/**
 * Returns the given distributor ID, or the distributor ID of the current user if no ID is provided.
 * @param {String} [id] - Distributor ID
 * @returns {String} distributor ID
 * @throws {Error} if no ID is provided AND the current user is unauthenticated
 * @memberof MySiteRestService
 * @private
 */
function fallbackToCurrentUserId(id) {
    if (!id) {
        let user = UserService.getUser();
        if (!user) {
            nuskin.events.publish(nuskin.events.authentication.REQUEST_FOR_LOGIN);
            // throw new Error('MySiteRestService Error: sponsor ID must be provided or user must be authenticated')
        };
        id = user.id;
    }
    return id;
}

/**
 * updates the cached My Site sponsor data of the current user from Firebase.
 * This function is called after edits are made to the user's My Site sponsor record.
 * @returns {Promise.<>} Promise that resolves to updated sponsor record
 * @memberof MySiteRestService
 * @private
 */
async function updateCachedSponsorData() {
    let fb = await getUserFirebaseConn();
    let id = getUser().id;
    let data = await fb.database().ref(`/${currentUserPath()}`)
        .once('value')
        .then(snapshot => snapshot.val());
    sponsors.set(id, data || null);
    return cloneSponsorRecord(sponsors.get(id));
}

/**
 * Retrieves a market's featured products data.
 * @param {Object} [options] - optional parameters:
 * @param {String} [options.country] - Country code; defaults to current country
 * @param {String} [options.language] - Language code; defaults to current language
 * @returns {Promise.<Object>}
 * @memberof MySiteRestService
 * @private
 */
async function getMarketFeaturedProductsData(options) {
    let cfg = ConfigService.getMarketConfig();
    let runCfg = RunConfigService.getRunConfig();

    const DEFAULT_OPTIONS = {
        country: runCfg.country,
        language: runCfg.language
    };
    options = Object.assign({}, DEFAULT_OPTIONS, options);
    let key = `${options.language}_${options.country}`;

    // Return cached data if available
    if (marketProducts.has(key)) return marketProducts.get(key);

    // Return cached promise if server request is currently in progress
    if (marketProductPromises.has(key)) return marketProductPromises.get(key);

    // No cached data and no cached request; make new server request
    let url = `${cfg.siteUrl}/apps/global/services/featured/products?market=${options.country}&language=${options.language}`;
    let promise = fetch(url, { method: 'GET', credentials: 'omit' })
        .then(transformFetchResponse)
        .then(data => {
            marketProducts.set(key, data);
            marketProductPromises.delete(key);
            return data;
        });
    marketProductPromises.set(key, promise);
    return promise;
}

/**
 * Transforms the response from the Fetch API into a JSON object
 * @param {Object} response
 * @returns {Promise.<Object>}
 * @throws {Object} throws response body as error if the response includes an HTTP error code
 * @memberof MySiteRestService
 * @private
 */
async function transformFetchResponse(response) {
    let json = await response.json();
    if (response.ok) return json;
    throw json;
}

/**
 * Returns a copy of the given object, with any undefined fields removed.
 * This is done for compatibility with the Firebase JavaScript API.
 * @param {*} obj - Object to clean
 * @returns {*} Cleaned copy of object
 * @memberof MySiteRestService
 * @private
 */
function toCleanedObj(obj) {
    if (obj instanceof Array) {
        return obj.map(field => toCleanedObj(field));
    } else if (obj instanceof Object) {
        let copy = Object.assign({}, obj);
        for (let key of Object.keys(copy)) {
            if (copy[key] === undefined) delete copy[key];
            else copy[key] = toCleanedObj(copy[key]);
        }
        return copy;
    } else {
        return obj;
    }
}

/**
 * Returns a clone of the given My Site sponsor record, to protect the original data.
 * @param {} sponsor
 * @returns {} clone of sponsor record
 * @memberof MySiteRestService
 * @private
 */
function cloneSponsorRecord(sponsor) {
    if (sponsor === null) return null;
    let obj = deepClone(sponsor);
    return obj;
}

function deepClone(obj) {
    if(Array.isArray(obj)) return obj.slice();
    obj = Object.assign({}, obj);
    let keys = Object.keys(obj);
    if(keys.length) {
        keys.forEach(key => {
            if(typeof obj[key] === 'object') obj[key] = deepClone(obj[key]) 
        });
    }
    return obj;
}
